import { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { messaging } from "../api";

// Rendu du corps HTML d'un e-mail, SANITIZÉ (anti-XSS) — comme Gmail. On garde la
// mise en forme (gras, listes, liens, images, styles inline) mais on supprime tout
// ce qui est exécutable (script, gestionnaires on*, iframes…). DOMPurify retire le
// dangereux par défaut ; un hook force l'ouverture des liens dans un nouvel onglet
// (rel=noopener) pour ne jamais détourner la fenêtre de l'app.

let hooked = false;
function ensureLinkHook(): void {
  if (hooked) return;
  hooked = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer nofollow");
    }
  });
}

function sanitize(html: string): string {
  ensureLinkHook();
  return DOMPurify.sanitize(html, {
    // Pas de script/iframe/object/embed/form — uniquement du contenu d'affichage.
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "style", "link", "meta"],
    FORBID_ATTR: ["onerror", "onload", "onclick"],
    // Les liens cibles non-http(s)/mailto sont retirés (anti javascript:).
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|cid:|data:image\/)/i,
  });
}

/**
 * Retire d'un attribut `style` les déclarations de POLICE et de TAILLE
 * (`font-family`, `font-size`, et la forme courte `font:`) pour que le corps suive
 * la police/taille de l'app (rendu homogène et propre sur tous les e-mails). On
 * garde tout le reste (couleur, graisse, alignement, marges…). Pur.
 */
function stripFontDecls(style: string): string {
  return style
    .split(";")
    .filter((decl) => {
      const prop = decl.split(":")[0]?.trim().toLowerCase() ?? "";
      return prop !== "font-family" && prop !== "font-size" && prop !== "font";
    })
    .join(";")
    .trim();
}

/**
 * Normalise le HTML d'un e-mail avant rendu :
 *  1. POLICE/TAILLE — retire les `font-family`/`font-size`/`font` inline et les
 *     attributs `face`/`size` des `<font>` → le corps suit la police de l'app.
 *  2. IMAGES — réécrit chaque <img> pour qu'elles CHARGENT sous la CSP (imgSrc
 *     'self') : distantes (http/https) → proxy backend (fetch serveur, anti-SSRF,
 *     masque l'IP, façon Gmail) ; inline `cid:` → route inline du message ; `data:`
 *     laissées telles quelles. Le `srcset` (qui rechargerait l'original distant)
 *     est retiré.
 * Best-effort : toute erreur → HTML inchangé. `messageId` résout les `cid:`.
 */
function normalizeEmailHtml(html: string, messageId: string | undefined): string {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");

    // 1. Police / taille : neutralise les styles inline et les attributs <font>.
    for (const el of Array.from(doc.querySelectorAll<HTMLElement>("[style]"))) {
      const cleaned = stripFontDecls(el.getAttribute("style") ?? "");
      if (cleaned) el.setAttribute("style", cleaned);
      else el.removeAttribute("style");
    }
    for (const f of Array.from(doc.querySelectorAll("font"))) {
      f.removeAttribute("face");
      f.removeAttribute("size");
    }

    // 2. Images : réécriture vers le proxy / la route inline.
    for (const img of Array.from(doc.querySelectorAll("img"))) {
      img.removeAttribute("srcset");
      const src = img.getAttribute("src")?.trim();
      if (!src) continue;
      if (/^cid:/i.test(src)) {
        const cid = src.slice(4).replace(/^<|>$/g, "").trim();
        if (messageId && cid) img.setAttribute("src", messaging.inlineImageUrl(messageId, cid));
        else img.removeAttribute("src"); // cid non résoluble → pas de requête cassée
      } else if (/^https?:\/\//i.test(src)) {
        img.setAttribute("src", messaging.emailImageUrl(src));
      }
      // data:image/… → laissé tel quel (autorisé par la CSP).
    }
    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

// Marqueurs de DÉBUT de citation (l'historique des messages précédents) — comme
// Gmail qui replie tout ce qui suit derrière un « ••• ». Couvre Gmail
// (.gmail_quote), Outlook/OWA (divRplyFwdMsg / appendonsend), et les blockquotes.
const QUOTE_SELECTOR = [
  ".gmail_quote",
  "blockquote",
  "#divRplyFwdMsg",
  "#appendonsend",
  '[id^="divRplyFwd"]',
  '[id^="x_divRplyFwd"]',
].join(",");

/**
 * Sépare le HTML en { main, quoted } : `main` est le nouveau message, `quoted` est
 * l'historique cité (replié par défaut). On localise le 1ᵉʳ marqueur de citation,
 * on remonte au niveau du body, et tout ce qui suit (lui inclus) devient `quoted`.
 * Si tout le contenu est cité (rien avant), on ne replie rien (évite de masquer un
 * message qui n'est qu'un transfert). Best-effort : toute erreur → pas de repli.
 */
function splitQuoted(html: string): { main: string; quoted: string | null } {
  try {
    const body = new DOMParser().parseFromString(html, "text/html").body;
    const boundary = body.querySelector(QUOTE_SELECTOR);
    if (!boundary) return { main: html, quoted: null };
    let node: Element = boundary;
    while (node.parentElement && node.parentElement !== body) node = node.parentElement;
    const quoted = document.createElement("div");
    const toMove: ChildNode[] = [];
    for (let cur: ChildNode | null = node; cur; cur = cur.nextSibling) toMove.push(cur);
    toMove.forEach((n) => quoted.appendChild(n));
    const main = body.innerHTML.trim();
    const quotedHtml = quoted.innerHTML.trim();
    if (!main) return { main: html, quoted: null }; // tout est cité → on ne replie pas
    return { main, quoted: quotedHtml || null };
  } catch {
    return { main: html, quoted: null };
  }
}

export function EmailHtml({ html, messageId }: { html: string; messageId?: string }) {
  // Sanitize d'abord (anti-XSS), PUIS réécrit les <img> vers le proxy/route inline
  // (sous la CSP 'self'), enfin replie l'historique cité.
  const { main, quoted } = useMemo(
    () => splitQuoted(normalizeEmailHtml(sanitize(html), messageId)),
    [html, messageId],
  );
  const [showQuoted, setShowQuoted] = useState(false);
  // Conteneur isolé : styles scopés via la classe .email-html (cf cockpit.css) —
  // images bornées, défilement horizontal pour les tableaux larges.
  return (
    <div className="email-html">
      <div dangerouslySetInnerHTML={{ __html: main }} />
      {quoted && (
        <>
          <button
            type="button"
            className="email-quote-toggle"
            aria-label={showQuoted ? "Masquer le contenu cité" : "Afficher le contenu cité"}
            aria-expanded={showQuoted}
            onClick={() => setShowQuoted((v) => !v)}
          >
            •••
          </button>
          {showQuoted && (
            <div className="email-quoted" dangerouslySetInnerHTML={{ __html: quoted }} />
          )}
        </>
      )}
    </div>
  );
}
